/**
 * Markdown rendering, keyed on `styles/components/markdown.css` (vendored byte-identical).
 *
 * The attribute contract that stylesheet selects on, and which this component must emit:
 *
 *   [data-component="markdown"]              root
 *   [data-markdown-block]                    one per streamed block
 *   [data-component="markdown-code"]         wraps a fenced block, + [data-code-kind]
 *   [data-slot="markdown-copy-button"]       + [data-copied], [data-copy-icon], [data-check-icon]
 *   [data-inline-code-kind="path"|"url"]     inline code naming a file or a URL
 *
 * Upstream patches the DOM incrementally with morphdom and highlights with shiki. Neither
 * is a dependency here, and `markdown-stream.ts` already splits the stream into stable
 * blocks so React re-renders only the block that changed — the same outcome by React's own
 * means. What is *not* reinvented is the markup: the attributes above are upstream's, on
 * upstream's elements, or the vendored CSS matches nothing.
 */

import { memo, useCallback, useMemo, useRef, type MouseEvent } from "react";
import { marked } from "marked";
import katex from "katex";
import DOMPurify from "dompurify";

import { useMarkdownCopy } from "../chat/react.tsx";
import { project, type Projection, type Block } from "./markdown-stream.ts";
import { inlineCodeKind } from "./vendored/markdown-inline-code-kind.ts";

export interface MarkdownProps {
  text: string;
  live?: boolean;
  className?: string;
}

/** Languages upstream treats as shell, which the stylesheet colours differently. */
const SHELL_LANGUAGES = new Set(["bash", "sh", "shell", "zsh", "console", "shell-session"]);

const COPY_ICON = `<svg data-copy-icon viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true"><path d="M5.5 5.5V2.5H13.5V10.5H10.5M2.5 5.5H10.5V13.5H2.5V5.5Z" stroke="currentColor"/></svg>`;
const CHECK_ICON = `<svg data-check-icon viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor"/></svg>`;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderMath(text: string): string {
  return text
    .replace(/\$\$(.*?)\$\$/gs, (_, math) => {
      try {
        return katex.renderToString(math, { displayMode: true, throwOnError: false });
      } catch {
        return math;
      }
    })
    .replace(/\$(.*?)\$/g, (_, math) => {
      try {
        return katex.renderToString(math, { displayMode: false, throwOnError: false });
      } catch {
        return math;
      }
    });
}

function codeBlockHtml(source: string, language?: string): string {
  const kind = language && SHELL_LANGUAGES.has(language.toLowerCase()) ? "shell" : undefined;
  const languageClass = language ? ` class="language-${escapeHtml(language)}"` : "";
  return (
    `<div data-component="markdown-code"${kind ? ` data-code-kind="${kind}"` : ""}>` +
    `<button type="button" data-slot="markdown-copy-button" data-copied="false" aria-label="Copy code">` +
    `${COPY_ICON}${CHECK_ICON}</button>` +
    // `.shiki` is what carries the block's background, padding, radius and border in
    // upstream's markdown.css — shiki emits it on the <pre> it generates. We do not run
    // shiki (no token colouring), but the container styling is the visible part, and
    // without this class a fenced block renders as bare text on the page background.
    `<pre class="shiki"><code${languageClass}>${escapeHtml(source)}</code></pre>` +
    `</div>`
  );
}

/**
 * `marked` renders inline code as a bare `<code>`; upstream tags it so a file path or a
 * URL reads differently from an identifier. Done in the renderer rather than by walking
 * the DOM afterwards, so the attribute is there on first paint.
 */
function inlineMarkdownHtml(source: string): string {
  const renderer = new marked.Renderer();

  // A settled message is one `full` block — `markdown-stream.ts` only splits fences out
  // while streaming — so without this a finished reply's code blocks reach `marked`'s
  // default renderer and lose the wrapper, the copy button and the `.shiki` container
  // styling. That is the common case, not the edge one.
  renderer.code = ({ text, lang }: { text: string; lang?: string }) =>
    codeBlockHtml(text, lang?.trim().split(/\s+/, 1)[0] || undefined);

  renderer.codespan = ({ text }: { text: string }) => {
    // `marked` hands over an already-escaped token; classify the decoded text.
    const decoded = text
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&");
    const kind = inlineCodeKind(decoded);
    return `<code${kind ? ` data-inline-code-kind="${kind}"` : ""}>${text}</code>`;
  };

  return marked.parse(renderMath(source), { async: false, renderer }) as string;
}

export function renderBlockHtml(block: Block): string {
  if (block.mode === "code") return codeBlockHtml(block.src, block.language);
  return DOMPurify.sanitize(inlineMarkdownHtml(block.src));
}

export const MarkdownBlockView = memo(function MarkdownBlockView({ block }: { block: Block }) {
  const html = useMemo(
    () => renderBlockHtml(block),
    [block.src, block.mode, block.language]
  );

  return (
    <div
      data-markdown-block=""
      data-mode={block.mode}
      // biome-ignore lint: prose is sanitised by DOMPurify; code is HTML-escaped
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

export function Markdown({ text, live = false, className }: MarkdownProps) {
  const projectionRef = useRef<Projection | undefined>(undefined);

  const projection = useMemo(() => {
    const next = project(projectionRef.current, text, live);
    projectionRef.current = next;
    return next;
  }, [text, live]);

  // The delegated target is captured while React still owns the event.
  const copy = useMarkdownCopy();
  const onClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    copy({ root: event.currentTarget, target: event.target });
  }, [copy]);

  return (
    <div data-component="markdown" className={className} onClick={onClick}>
      {projection.blocks.map((block, index) => (
        <MarkdownBlockView
          key={`${index}:${block.mode}:${block.raw.length}`}
          block={block}
        />
      ))}
    </div>
  );
}
